import React, { useEffect, useState } from 'react';

import { triggerBingAnalytics } from 'src/services/apiRequests/commerce/bingAnalytics';
import { ICommerceAsset } from 'src/services/responseTypes/commerceResponse';
import { CommerceSideBarProps } from 'src/types/commerce';
import { getSessionStorage } from 'src/utils/browserStorageHandler';
import {
  getCommerceWishlistedProductsKeys,
  intersectionCallback,
  isServer
} from 'src/utils/common';
import { Commerce } from 'src/utils/constants/CommerceConstants';
import { COMMERCE_WISHLISTED_PRODUCTS } from 'src/utils/constants/localStorageConstants';

import CommerceCard from './../CommerceCard';
import {
  Ad,
  Adwrapper,
  CommerceItems,
  Header,
  IconWrapper,
  Title,
  closeIcon,
  commerceContainer,
  shopIcon
} from './styles';
import EmptyWishlistBanner from '../EmptyWishlistBanner';

const CommerceSideBar: React.FC<CommerceSideBarProps> = ({
  data = [],
  translations = {},
  onPlayer = false,
  showWishlist = false,
  pageLoadPingUrl = '',
  commerceWishlistedProductsLength = 0,
  setShowToast,
  closeLayout,
  closeIconClick,
  handleCommerceLayout,
  setCommerceWishlistedProductsLength
}) => {
  const [observer, setObserver] = useState<IntersectionObserver>();
  const [sideBarData, setSideBarData] = useState<Array<ICommerceAsset>>(data);
  const dataLength = data?.length || 0;
  const {
    plex: {
      ContextualCommerce_Wishlist = '',
      ContextualCommerce_BannerCTA = ''
    } = {}
  } = translations;

  const wishlistText = sideBarData?.length
    ? ContextualCommerce_Wishlist + ' (' + sideBarData?.length?.toString() + ')'
    : ContextualCommerce_Wishlist;

  useEffect(() => {
    if (!isServer()) {
      const observer = new IntersectionObserver(intersectionCallback, {
        threshold: 1
      });
      setObserver(observer);
    }
  }, []);

  // Call pageLoadPingUrl once new set of commerce data arrives
  useEffect(() => {
    if (pageLoadPingUrl) {
      triggerBingAnalytics(pageLoadPingUrl);
    }
  }, [pageLoadPingUrl]);

  useEffect(() => {
    if (observer && sideBarData?.length) {
      const cards = document.querySelectorAll(`#${Commerce.INTERSECTION_ID}`);
      cards.forEach((card) => {
        observer?.observe(card);
      });
    }
  }, [observer, sideBarData?.length]);

  useEffect(() => {
    if (showWishlist) {
      const productsWishlisted =
        getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || [];
      setSideBarData(productsWishlisted);
    } else {
      setSideBarData(data);
    }
  }, [showWishlist, commerceWishlistedProductsLength]);

  useEffect(() => {
    if (!showWishlist && dataLength) {
      setSideBarData(data);
    }
  }, [showWishlist, dataLength]);

  const renderCommerceAssets = () => {
    const wishlistedProductsKeys = getCommerceWishlistedProductsKeys();
    return sideBarData?.map((asset: ICommerceAsset) => (
      <CommerceCard
        Id={Commerce.INTERSECTION_ID}
        key={asset?.url} // temporary
        {...asset}
        showWishlist={showWishlist}
        onPlayer={onPlayer}
        wishlisted={wishlistedProductsKeys?.includes(asset?.id)}
        translations={translations}
        setPlayerShowToast={setShowToast}
        setSideBarData={setSideBarData}
        setCommerceWishlistedProductsLength={
          setCommerceWishlistedProductsLength
        }
      />
    ));
  };

  return (
    <div css={commerceContainer(onPlayer)}>
      <Header>
        <span css={shopIcon} title={Commerce.SHOP_ICON} />
        <Title>
          {showWishlist ? wishlistText : ContextualCommerce_BannerCTA}
        </Title>
        <Adwrapper>
          <Ad>{Commerce.Ad}</Ad>
        </Adwrapper>
        <IconWrapper onClick={onPlayer ? closeLayout : closeIconClick}>
          <span css={closeIcon} title={Commerce.CLOSE_ICON} />
        </IconWrapper>
      </Header>
      {showWishlist && !sideBarData?.length ? (
        <EmptyWishlistBanner handleCommerceLayout={handleCommerceLayout} />
      ) : (
        <CommerceItems>{renderCommerceAssets()}</CommerceItems>
      )}
    </div>
  );
};

export default CommerceSideBar;
